import {
    useMemo,
    useCallback,
    useState,
    useRef,
    useEffect,
    MutableRefObject,
    type JSX,
} from "react";

import { FAStringInput } from "@impero-com/common/components/form/formAtomFields/faStringInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useCompoundFormState } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { ToFormAtoms } from "@impero-com/common/utils/form/formAtom/typeUtils";
import { t } from "@impero-com/common/utils/i18n";
import { ButtonGroup, ButtonSpec, Divider } from "@impero-com/ui";
import { PrimitiveAtom, atom, useAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import * as Yup from "yup";

import {
    NewUserTagCategoryWithTags,
    UserTagCategoryWithTagsView,
} from "api/generated/model";
import { Drawer } from "components/drawer";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { TagManagementTable } from "components/tag/tagManagementTable";

import { CancelConfirmationDialog } from "./components/CancelConfirmationDialog";
import { useCreateTagCategoryMutation } from "./queries/useCreateTagCategoryMutation";

const MAX_CATEGORY_NAME_LENGTH = 255;

export interface CreateTagCategoryDrawerProps {
    // Tag categories that already exists. Is used for validation purposes.
    existingTagCategoryNames: Array<string>;

    // A callback is invoked when the drawer is closed or the cancel button is clicked
    onCancel: () => void;

    // A callback is invoked when a new tag category and tags are created.
    onCreated: (categoryWithTags: UserTagCategoryWithTagsView) => void;
}

const CreateTagCategoryDrawer = ({
    existingTagCategoryNames,
    onCancel,
    onCreated,
}: CreateTagCategoryDrawerProps): JSX.Element => {
    const { mutateAsync: createTagCategory } =
        useCreateTagCategoryMutation(onCreated);

    // Use Ref to avoid creation of a new form atoms within useNewCategoryFormAtoms() whenever existingTagCategoryNames change.
    const existingTagCategoryNamesRef = useRef(existingTagCategoryNames);
    useEffect(() => {
        existingTagCategoryNamesRef.current = existingTagCategoryNames;
    }, [existingTagCategoryNames]);

    const { newCategoryFormAtoms, newCategoryAtom } = useNewCategoryFormAtoms(
        existingTagCategoryNamesRef
    );
    const { dirty: formDirty, validate: validateForm } = useCompoundFormState(
        newCategoryFormAtoms,
        newCategoryAtom
    );
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const isLoading = status === "loading";

    const handleSubmit = useCallback(async () => {
        const result = await validateForm();
        if (result.type === "SUCCESS") {
            const newCategoryPayload: NewUserTagCategoryWithTags =
                result.values;
            createTagCategory({ data: newCategoryPayload });
        } else {
            return;
        }
    }, [validateForm, createTagCategory]);

    const onCancelWithConfirmation = useCallback(() => {
        if (formDirty) {
            setShowCancelConfirmation(true);
        } else {
            onCancel();
        }
    }, [setShowCancelConfirmation, onCancel, formDirty]);

    const buttons: ButtonSpec[] = useMemo(() => {
        return [
            {
                id: "create",
                label: t("TagsList_CreateCategoryDrawer_Action_CreateCategory"),
                type: "primary",
                onClick: () => void handleSubmit(),
                disabled: isLoading,
                loading: isLoading,
            },
            {
                id: "cancel",
                label: t("TagsList_CreateCategoryDrawer_Action_Cancel"),
                type: "link",
                onClick: onCancelWithConfirmation,
                disabled: isLoading,
            },
        ];
    }, [isLoading, handleSubmit, onCancelWithConfirmation]);

    return (
        <>
            <Drawer
                buildHeader={() => t("TagsList_CreateCategoryDrawer_Header")}
                buildFooter={() => <ButtonGroup buttonSpecs={buttons} />}
                onCancel={onCancelWithConfirmation}
            >
                <BaseFormDisplay
                    label={t("TagsList_EditCategoryDrawer_Field_CategoryName")}
                >
                    <FAStringInput
                        atom={newCategoryFormAtoms.categoryNameAtom}
                    />
                </BaseFormDisplay>
                <Divider />
                <BaseFormDisplay
                    label={t("TagsList_EditCategoryDrawer_Field_Tags")}
                >
                    <TagTable
                        tagNamesAtom={newCategoryFormAtoms.tagNamesAtom}
                    />
                </BaseFormDisplay>
            </Drawer>

            <CancelConfirmationDialog
                open={showCancelConfirmation}
                onConfirm={onCancel}
                onAbort={() => setShowCancelConfirmation(false)}
            />
        </>
    );
};

interface TagTableProps {
    tagNamesAtom: FormAtom<Array<string>>;
}

const TagTable = (props: TagTableProps): JSX.Element => {
    const { tagNamesAtom } = props;
    const [tagNames, setTagNames] = useAtom(tagNamesAtom);

    const onAdd = useCallback(
        (tagName: string): void => {
            void setTagNames((names) => [tagName, ...names]);
        },
        [setTagNames]
    );

    const onDelete = useCallback(
        (tagName: string) => {
            void setTagNames((names) => {
                return names.filter((name) => name !== tagName);
            });
        },
        [setTagNames]
    );

    const onRename = useCallback(
        (oldName: string, newName: string) => {
            void setTagNames((names) => {
                return names.map((name) => {
                    if (name === oldName) {
                        return newName;
                    } else {
                        return name;
                    }
                });
            });
        },
        [setTagNames]
    );

    return (
        <TagManagementTable
            tags={tagNames}
            getTagId={identity}
            getTagName={identity}
            askConfirmationOnDelete={false}
            onRename={onRename}
            onDelete={onDelete}
            onAdd={onAdd}
        />
    );
};

const identity = <T,>(value: T): T => value;

interface UseNewCategoryFormAtomsOutput {
    newCategoryFormAtoms: ToFormAtoms<NewUserTagCategoryWithTags>;
    newCategoryAtom: PrimitiveAtom<NewUserTagCategoryWithTags>;
}

const useNewCategoryFormAtoms = (
    existingTagCategoryNamesRef: MutableRefObject<Array<string>>
): UseNewCategoryFormAtomsOutput => {
    const newCategoryAtom = useMemo(() => {
        return atom<NewUserTagCategoryWithTags>({
            categoryName: "",
            tagNames: [],
        });
    }, []);

    const categoryNameAtom = useMemo(() => {
        const fieldAtom = focusAtom(newCategoryAtom, (o) =>
            o.prop("categoryName")
        );
        const validationSchema = Yup.string()
            .trim()
            .required(
                t(
                    "TagsList_CreateCategoryDrawer_CategoryName_Error_CannotBeBlank"
                )
            )
            .test(
                "too-long",
                (_d) =>
                    t(
                        "TagsList_CreateCategoryDrawer_CategoryName_Error_TooLong"
                    ),
                (value) => {
                    if (value !== undefined) {
                        return value.length <= MAX_CATEGORY_NAME_LENGTH;
                    } else {
                        return true;
                    }
                }
            )
            .test(
                "already-exists",
                (_d) =>
                    t(
                        "TagsList_CreateCategoryDrawer_CategoryName_Error_AlreadyExists"
                    ),
                // eslint-disable-next-line react-hooks/refs
                (value) => {
                    if (value !== undefined) {
                        const lcCategoryNames =
                            existingTagCategoryNamesRef.current.map((name) =>
                                name.toLowerCase()
                            );
                        return !lcCategoryNames.includes(value.toLowerCase());
                    } else {
                        return true;
                    }
                }
            );
        return formAtom(fieldAtom, validationSchema);
    }, [newCategoryAtom, existingTagCategoryNamesRef]);

    const tagNamesAtom = useMemo(() => {
        const fieldAtom = focusAtom(newCategoryAtom, (o) => o.prop("tagNames"));
        // Note: TagManagementTable internally validates tag names,
        // so there is no need to repeat sophisticated validation here.
        const validationSchema = Yup.array().defined();
        return formAtom(fieldAtom, validationSchema);
    }, [newCategoryAtom]);

    const newCategoryFormAtoms: ToFormAtoms<NewUserTagCategoryWithTags> =
        useMemo(() => {
            return {
                categoryNameAtom,
                tagNamesAtom,
            };
        }, [categoryNameAtom, tagNamesAtom]);

    return {
        newCategoryFormAtoms,
        newCategoryAtom,
    };
};

export { CreateTagCategoryDrawer };
